import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import { forgetAiSettings, loadAiSettings } from '../utils/aiThresholds';

/*
 * The AI certainty thresholds, on the expert review page and on MLOps' Metrics
 * tab (a system administrator cannot open the review page). Everyone who can
 * open either page sees them and the measured precision behind them; collection
 * managers and internal experts can change them (the server enforces who).
 */
const pct = v => (v === null || v === undefined ? '—' : `${Math.round(v * 1000) / 10}%`);
const toPercentText = v => String(Math.round(v * 100));
const parseLevels = text => text.split(/[,\s]+/).filter(Boolean).map(x => Number(x) / 100);

export default function AiThresholdsPanel() {
  const { t } = useTranslation(['review', 'ai']);
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState(null);
  const [status, setStatus] = useState({ saving: false, message: '', error: '' });

  const adopt = (s) => {
    setSettings(s);
    setForm({
      suggest: toPercentText(s.thresholds.suggest_min),
      high: toPercentText(s.thresholds.high_certainty_min),
      levels: s.thresholds.game_queue_levels.map(toPercentText).join(', '),
    });
  };

  useEffect(() => {
    let live = true;
    loadAiSettings({ refresh: true }).then(s => { if (live) adopt(s); });
    return () => { live = false; };
  }, []);

  if (!settings || !form) return null;

  const save = async (e) => {
    e.preventDefault();
    setStatus({ saving: true, message: '', error: '' });
    try {
      const { data } = await api.put('/settings/ai-thresholds', {
        suggest_min: Number(form.suggest) / 100,
        high_certainty_min: Number(form.high) / 100,
        game_queue_levels: parseLevels(form.levels),
      });
      forgetAiSettings();
      adopt({ ...settings, ...data, thresholds: data.thresholds });
      setStatus({ saving: false, message: t('thresholds.saved'), error: '' });
    } catch (err) {
      setStatus({ saving: false, message: '',
                  error: t('thresholds.failed', { error: err?.response?.data?.error || '' }) });
    }
  };

  const { thresholds, measured, can_edit: canEdit } = settings;
  const current = new Set([thresholds.suggest_min, thresholds.high_certainty_min]);
  const field = (key, label) => (
    <label style={labelStyle}>
      {label}
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        <input type="number" min="1" max="100" step="1" value={form[key]} disabled={!canEdit}
               onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
               title={key === 'suggest' ? t('ai:tip.thresholds.suggest') : t('ai:tip.thresholds.high')}
               aria-label={label} style={{ ...inputStyle, width: 70 }} />
        %
      </span>
    </label>
  );

  return (
    <details data-testid="ai-thresholds" style={panelStyle}>
      <summary style={summaryStyle} title={t('ai:tip.thresholds.panel')}>{t('thresholds.title')}</summary>
      <p style={mutedStyle}>{t('thresholds.neverAutomatic')}</p>
      <form onSubmit={save} style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        {field('suggest', t('thresholds.suggest'))}
        {field('high', t('thresholds.high'))}
        <label style={labelStyle}>
          {t('thresholds.levels')}
          <input value={form.levels} disabled={!canEdit} aria-label={t('thresholds.levels')}
                 onChange={e => setForm(f => ({ ...f, levels: e.target.value }))}
                 title={t('ai:tip.thresholds.levels')}
                 placeholder={t('thresholds.levelsHint')} style={{ ...inputStyle, width: 140 }} dir="ltr" />
        </label>
        {canEdit && (
          <button type="submit" disabled={status.saving} title={t('ai:tip.thresholds.save')} style={buttonStyle}>
            {status.saving ? t('thresholds.saving') : t('thresholds.save')}
          </button>
        )}
      </form>
      {!canEdit && <p style={mutedStyle}>{t('thresholds.readOnly')}</p>}
      {status.message && <p role="status" style={okStyle}>{status.message}</p>}
      {status.error && <p role="alert" style={errorStyle}>{status.error}</p>}

      <h4 style={{ margin: '12px 0 4px', fontSize: 13 }}>{t('thresholds.measuredTitle')}</h4>
      {!measured ? <p style={mutedStyle}>{t('thresholds.measuredNone')}</p> : (
        <>
          <p style={mutedStyle}>{t('thresholds.modelVersion', { version: measured.model_version || '?', n: measured.n })}</p>
          {['suggest_min', 'high_certainty_min'].map(key => !measured.at?.[key] && (
            <p key={key} style={mutedStyle}>{t('thresholds.notMeasuredAt', { pct: Math.round(thresholds[key] * 100) })}</p>
          ))}
          <div className="table-wrap">
            <table data-testid="ai-thresholds-measured" style={{ borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr>
                  <th style={cellStyle}>{t('thresholds.colThreshold')}</th>
                  <th style={cellStyle}>{t('thresholds.colAnswered')}</th>
                  <th style={cellStyle}>{t('thresholds.colPrecision')}</th>
                </tr>
              </thead>
              <tbody>
                {(measured.rows || []).map(r => (
                  <tr key={r.threshold} style={current.has(r.threshold) ? { background: 'var(--c-primary-soft)', fontWeight: 600 } : undefined}>
                    <td style={cellStyle}>{Math.round(r.threshold * 100)}%</td>
                    <td style={cellStyle}>{pct(r.answered_fraction)}</td>
                    <td style={cellStyle}>{pct(r.precision)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <ul style={{ ...mutedStyle, paddingInlineStart: 18 }}>
            {(measured.caveats || []).map(c => <li key={c} dir="ltr">{c}</li>)}
          </ul>
        </>
      )}
    </details>
  );
}

const panelStyle = { border: '1px solid var(--c-border)', borderRadius: 8, padding: 12, background: 'var(--c-surface)', marginTop: 16 };
const summaryStyle = { cursor: 'pointer', fontWeight: 600, color: 'var(--c-primary-fg)' };
const mutedStyle = { fontSize: 12, color: 'var(--c-text-muted-fg)', margin: '6px 0' };
const labelStyle = { display: 'flex', flexDirection: 'column', gap: 2, fontSize: 12, color: 'var(--c-text-body-fg)' };
const inputStyle = { fontSize: 14, padding: '4px 6px', borderRadius: 4, border: '1px solid var(--c-border-strong)' };
const buttonStyle = { background: 'var(--c-primary)', color: 'var(--c-on-accent)', border: 'none', borderRadius: 6, padding: '6px 14px', cursor: 'pointer', fontSize: 14 };
const okStyle = { fontSize: 13, color: 'var(--c-success-fg)', margin: '6px 0' };
const errorStyle = { fontSize: 13, color: 'var(--c-danger-fg)', background: 'var(--c-danger-soft)', borderRadius: 6, padding: '6px 10px' };
const cellStyle = { padding: '3px 8px', borderBottom: '1px solid var(--c-border)', textAlign: 'start' };
